import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { MouseEvent, useMemo } from 'react';

import type { SearchUrlParams } from '@/types/search/SearchUrlParams';

import { useNavigateSearchPageWithParams } from '@/pages/Search/SearchPage/hooks/useNavigateSearchPageWithParams';
import { SearchUrlStatus } from '@/types/search/enums/searchUrlStatus';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { FilterAction } from '@/types/SearchFacet';
import { getSearchUrlWithParams } from '@/utils/urls';

type Props = {
    isArchivedSearch: boolean;
    searchTerm: string;
    totalSoldItems: number;
};

export const NoFilterTermResults = ({ isArchivedSearch, searchTerm, totalSoldItems }: Props) => {
    const { formatMessage } = useIntl();
    const navigateWithParams = useNavigateSearchPageWithParams();

    const priceResultsUrlParams = useMemo((): SearchUrlParams => {
        return {
            keyword: searchTerm,
            sort: SearchSortAndDirection.MostRelevant,
            status: SearchUrlStatus.Archive,
        };
    }, [searchTerm]);

    const onNavigateToPriceResults = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        navigateWithParams({
            filterAction: FilterAction.ViewPriceResults,
            params: priceResultsUrlParams,
            preventScrollReset: true,
        });
    };

    return (
        <div className="flex flex-col justify-center gap-sm py-md text-center">
            <h2 className="hui-text-h3 whitespace-pre-line">
                {formatMessage({
                    id: isArchivedSearch
                        ? 'searchPage.noFilterResults.titleArchive'
                        : 'searchPage.noFilterResults.title',
                })}
            </h2>
            {!isArchivedSearch && totalSoldItems > 0 && (
                <div className="inline-flex flex-wrap items-center justify-center gap-x-xs">
                    <BodyPrimary>
                        {formatMessage(
                            { id: 'searchPage.noFilterResults.viewPriceResultsText' },
                            { searchTerm, totalSoldItems }
                        )}
                    </BodyPrimary>
                    <Link
                        href={getSearchUrlWithParams(priceResultsUrlParams)}
                        onClick={onNavigateToPriceResults}
                    >
                        {formatMessage({ id: 'searchPage.noFilterResults.viewPriceResultsLink' })}
                    </Link>
                </div>
            )}
        </div>
    );
};
